import { BaseElement } from 'base';
import { bus } from 'bus';
import { isEmail } from 'is-email';

const SUCCESS_DURATION = 3000;
const SUBSCRIBED_KEY = 'footer:subscribed';
const PENDING_KEY = 'footer:subscribe-pending';
const SUBSCRIBED_LIMIT = 20;
const PENDING_TTL = 5 * 60 * 1000;
const LOCALE_TOPIC = 'locale-selector:active';
const IN_VIEW_TOPIC = 'footer:in-view';
const BOTTOM_INSET_TOPIC = 'viewport:bottom-inset';

const hashEmail = email => {
  let hash = 0x811c9dc5;
  for (const char of email) {
    hash ^= char.codePointAt(0);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
};

class SiteFooter extends BaseElement {
  onConnect() {
    this.form = this.q('.footer__form');
    this.emailSlot = this.q('[data-footer-email]');
    this.legalToggle = this.q('[data-footer-legal-toggle]');
    this.legalList = this.q('[data-footer-legal-list]');

    if (this.form) {
      this.listen(this, 'submit', this.onSubmit, { capture: true });
      this.listen(this.form, 'formdata', this.onFormData);
      this.listen(this.form, 'input', this.onInput);
    }
    if (this.legalToggle && this.legalList)
      this.listen(this.legalToggle, 'click', this.toggleLegal);
    this.qa('[data-footer-privacy]').forEach(control =>
      this.listen(control, 'click', this.onPrivacySettings)
    );
    this.bindBackToTop();
    this.bindLocale();
    this.revealForm();
    this.bindViewport();

    this.cleanup(() => clearTimeout(this.successTimer));
    const pending = this.takePending();
    if (this.emailSlot?.dataset.subscribed === 'true') {
      if (pending) this.rememberSubscribed(pending);
      this.afterSubscribe();
    }
  }

  get field() {
    return this.q('[data-footer-email] text-field');
  }

  revealForm() {
    if (!this.form?.id || window.location.hash !== `#${this.form.id}`) return;
    const navigation = performance.getEntriesByType('navigation')[0]?.type;
    if (navigation === 'reload' || navigation === 'back_forward') return;
    this.form.scrollIntoView({ behavior: 'instant' });
    if (document.readyState === 'complete') return;
    const settled = window.scrollY;
    this.listen(
      window,
      'load',
      () => {
        if (window.scrollY === settled)
          this.form.scrollIntoView({ behavior: 'instant' });
      },
      { once: true }
    );
  }

  bindViewport() {
    this.observe(
      new IntersectionObserver(entries =>
        bus.publish(IN_VIEW_TOPIC, entries.at(-1).isIntersecting)
      ),
      this
    );
    this.cleanup(() => bus.publish(IN_VIEW_TOPIC, false));
    this.subscribe(BOTTOM_INSET_TOPIC, this.applyBottomInset);
  }

  applyBottomInset(inset) {
    if (Number.isFinite(inset) && inset > 0) {
      this.style.setProperty('--footer-bottom-inset', `${Math.ceil(inset)}px`);
    } else {
      this.style.removeProperty('--footer-bottom-inset');
    }
  }

  async bindBackToTop() {
    const button = this.q('.footer__top');
    if (!button) return;
    await customElements.whenDefined('action-button');
    if (!this.isConnected) return;
    button.run(() => this.scrollToTop());
  }

  async bindLocale() {
    const trigger = this.q('.footer__locale');
    const dialog = this.dataset.localeDialog;
    if (!trigger || !dialog) return;
    await customElements.whenDefined('select-field');
    if (!this.isConnected) return;
    trigger.run(() => bus.publish(LOCALE_TOPIC, dialog));
    this.subscribe(LOCALE_TOPIC, active =>
      trigger.setExpanded(active === dialog)
    );
  }

  get email() {
    return (this.field?.value ?? '').trim().toLowerCase();
  }

  onSubmit(event) {
    if (event.target !== this.form) return;
    const field = this.field;
    if (typeof field?.setError !== 'function') return;

    const email = this.email;
    let error = '';
    if (!email) error = this.dataset.errorRequired;
    else if (!isEmail(email)) error = this.dataset.errorInvalid;

    if (error) {
      event.preventDefault();
      event.stopPropagation();
      field.setError(error);
      field.focus();
      return;
    }

    const hash = hashEmail(email);
    if (this.readSubscribed().includes(hash)) {
      event.preventDefault();
      event.stopPropagation();
      field.setSuccess(this.dataset.alreadySubscribed);
      this.clearSuccessLater();
      return;
    }

    this.writePending(hash);
  }

  readSubscribed() {
    let raw = null;
    try {
      raw = localStorage.getItem(SUBSCRIBED_KEY);
    } catch {
      return [];
    }
    if (!raw) return [];
    try {
      const stored = JSON.parse(raw);
      if (Array.isArray(stored)) return stored;
    } catch {}
    try {
      localStorage.removeItem(SUBSCRIBED_KEY);
    } catch {}
    return [];
  }

  rememberSubscribed(hash) {
    const next = [hash, ...this.readSubscribed().filter(item => item !== hash)];
    try {
      localStorage.setItem(
        SUBSCRIBED_KEY,
        JSON.stringify(next.slice(0, SUBSCRIBED_LIMIT))
      );
    } catch {}
  }

  takePending() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      return null;
    }
    try {
      const { h, t: savedAt } = JSON.parse(raw) ?? {};
      const age = Date.now() - savedAt;
      if (typeof h === 'string' && age >= 0 && age < PENDING_TTL) return h;
    } catch {}
    return null;
  }

  writePending(hash) {
    try {
      sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ h: hash, t: Date.now() })
      );
    } catch {}
  }

  onFormData(event) {
    const email = this.email;
    if (email) event.formData.set('contact[email]', email);
  }

  onInput() {
    const field = this.field;
    if (field?.getAttribute('data-state') === 'error') field.clearError();
  }

  clearSuccessLater() {
    clearTimeout(this.successTimer);
    this.successTimer = setTimeout(
      () => this.field?.clearSuccess?.(),
      SUCCESS_DURATION
    );
  }

  afterSubscribe() {
    this.clearSuccessLater();

    const formId = this.dataset.klaviyoForm;
    if (!formId || window.Shopify?.designMode) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  toggleLegal() {
    const open = this.legalToggle.getAttribute('aria-expanded') !== 'true';
    this.legalToggle.setAttribute('aria-expanded', String(open));
    this.legalList.hidden = !open;
  }

  onPrivacySettings() {
    try {
      window.OneTrust.ToggleInfoDisplay();
    } catch (error) {
      console.error('[footer] privacy settings cannot open:', error);
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0 });
  }
}

if (!customElements.get('site-footer'))
  customElements.define('site-footer', SiteFooter);
